import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Logo } from './Logo.js';

/**
 * Destino del CTA de demo. En la app real apunta a la ruta interna /acceso.
 * En el build estático de marketing (Hostinger) se define VITE_DEMO_HREF
 * (p. ej. un mailto), porque ahí no corre el backend de la demo.
 */
const DEMO_HREF = import.meta.env.VITE_DEMO_HREF as string | undefined;

function CtaDemo({ className, children }: { className?: string; children: ReactNode }) {
  if (DEMO_HREF) {
    return (
      <a className={className} href={DEMO_HREF}>
        {children}
      </a>
    );
  }
  return (
    <Link to="/acceso" className={className}>
      {children}
    </Link>
  );
}

/** Landing comercial de Ausentia (ruta /). Vende el producto y empuja a la demo. */
export function Landing() {
  return (
    <div className="lp">
      <header className="lp-nav">
        <div className="lp-nav-inner">
          <Link to="/" className="lp-logo">
            <Logo size={36} />
            <span className="lp-logo-text">
              <span className="lp-logo-name">Ausentia</span>
              <span className="lp-logo-by">by AtuelSoft</span>
            </span>
          </Link>
          <nav className="lp-nav-links" aria-label="Secciones">
            <a href="#como-funciona">Cómo funciona</a>
            <a href="#roles">Roles</a>
            <a href="#diferenciadores">Diferenciadores</a>
            <a href="#oferta">Oferta</a>
            <CtaDemo className="lp-cta">Ver demo</CtaDemo>
          </nav>
        </div>
      </header>

      <main>
        {/* Hero */}
        <section className="lp-seccion lp-hero" aria-labelledby="lp-hero-title">
          <div className="lp-seccion-inner lp-hero-grid">
            <div className="lp-hero-copy anim-fade">
              <p className="lp-eyebrow">Control médico laboral, sin perder trazabilidad</p>
              <h1 id="lp-hero-title">Auditá las ausencias por enfermedad, rápido y desde tu mano.</h1>
              <p className="lp-lead">
                Ausentia coordina la visita médica domiciliaria, registra el dictamen de aptitud y emite una constancia
                con firma electrónica y trazabilidad. RRHH ve si el trabajador está apto o no para trabajar; el
                diagnóstico queda reservado al servicio médico.
              </p>
              <div className="lp-cta-row">
                <CtaDemo className="lp-cta">Ingresar a la demo</CtaDemo>
                <a className="lp-cta-fantasma" href="#como-funciona">Ver cómo funciona</a>
              </div>
              <p className="mini lp-hero-nota">Demo con datos de ejemplo · sin instalación</p>
              <ul className="lp-pruebas" aria-label="Características principales">
                <li>Privacidad clínica por rol</li>
                <li>Operación aun sin señal</li>
                <li>Constancias trazables</li>
              </ul>
            </div>

            {/* Maqueta de constancia (solo divs, sin imágenes) */}
            <div className="lp-constancia" aria-hidden="false">
              <div className="lp-constancia-cab">
                <strong>Constancia de control médico</strong>
                <span className="insignia verde">Firmada</span>
              </div>
              <div className="lp-constancia-meta">
                <span>Control Nº 004182</span>
                <span>Hoy · 10:42</span>
              </div>
              <div className="lp-constancia-chip">
                <span className="insignia ambar">NO APTO</span>
              </div>
              <div className="lp-constancia-fila"><span>Realizó</span><strong>Dr. García · MP 12345</strong></div>
              <div className="lp-constancia-fila"><span>Vía</span><strong>Domicilio</strong></div>
              <div className="lp-constancia-fila"><span>Código</span><strong>AUS-7F3K-9QM2</strong></div>
              <div className="lp-constancia-qr">
                <div className="lp-qr-mock" aria-hidden="true">
                  {Array.from({ length: 49 }).map((_, i) => (
                    <span key={i} className={QR_PATRON[i] ? 'on' : ''} />
                  ))}
                </div>
                <span className="visually-hidden">Representación decorativa del código de la constancia</span>
              </div>
              <div className="lp-constancia-sello"><span aria-hidden="true" />Trazabilidad registrada</div>
              <p className="mini">Cada constancia lleva un código único de trazabilidad.</p>
            </div>
          </div>
        </section>

        {/* El problema */}
        <section className="lp-seccion" id="problema" aria-labelledby="lp-problema-title">
          <div className="lp-seccion-inner">
            <h2 id="lp-problema-title">El art. 210 da derecho a controlar. El problema es hacerlo bien.</h2>
            <div className="lp-grid-auto">
              <article className="tarjeta lp-feature">
                <h3>Privacidad del trabajador</h3>
                <p className="muted">El empleador puede auditar la licencia médica, pero el trabajador conserva su privacidad clínica.</p>
              </article>
              <article className="tarjeta lp-feature">
                <h3>Constancias en papel</h3>
                <p className="muted">Se reutilizan entre empresas, se falsifican y son difíciles de auditar.</p>
              </article>
              <article className="tarjeta lp-feature">
                <h3>Circuito manual</h3>
                <p className="muted">El ida y vuelta entre RRHH, operador y médico es lento y sin trazabilidad.</p>
              </article>
            </div>
          </div>
        </section>

        {/* Cómo funciona */}
        <section className="lp-seccion lp-seccion-alt" id="como-funciona" aria-labelledby="lp-como-title">
          <div className="lp-seccion-inner">
            <h2 id="lp-como-title">Cómo funciona</h2>
            <ol className="lp-pasos">
              <li className="lp-paso">
                <span className="lp-paso-num">1</span>
                <h3>Se solicita el control</h3>
                <p className="muted">La empresa pide auditar una ausencia; el operador asigna un médico auditor.</p>
              </li>
              <li className="lp-paso">
                <span className="lp-paso-num">2</span>
                <h3>Visita médica</h3>
                <p className="muted">El médico evalúa y dictamina si el trabajador está apto o no para trabajar.</p>
              </li>
              <li className="lp-paso">
                <span className="lp-paso-num">3</span>
                <h3>Doble firma</h3>
                <p className="muted">Firma grafométrica del trabajador en el dispositivo y firma electrónica del médico mediante código de un solo uso (OTP).</p>
              </li>
              <li className="lp-paso">
                <span className="lp-paso-num">4</span>
                <h3>Constancia trazable</h3>
                <p className="muted">Se emite una constancia con código único y bitácora encadenada de auditoría.</p>
              </li>
            </ol>
          </div>
        </section>

        {/* Por rol */}
        <section className="lp-seccion" id="roles" aria-labelledby="lp-roles-title">
          <div className="lp-seccion-inner">
            <h2 id="lp-roles-title">Una vista para cada rol</h2>
            <div className="lp-grid-auto lp-roles">
              <article className="tarjeta lp-feature">
                <div className="lp-feature-ic" aria-hidden="true">📋</div>
                <h3>Médico auditor</h3>
                <p className="muted">App móvil offline-first: agenda, dictamen, firma del trabajador y modo contingencia con acta.</p>
              </article>
              <article className="tarjeta lp-feature">
                <div className="lp-feature-ic" aria-hidden="true">🏢</div>
                <h3>RRHH de la empresa</h3>
                <p className="muted">Ve si el trabajador está apto o no para trabajar, quién realizó el control y por qué vía. Nunca el diagnóstico.</p>
              </article>
              <article className="tarjeta lp-feature">
                <div className="lp-feature-ic" aria-hidden="true">🩺</div>
                <h3>Servicio médico</h3>
                <p className="muted">Acceso a la versión clínica con observaciones reservadas, bajo control de acceso.</p>
              </article>
              <article className="tarjeta lp-feature">
                <div className="lp-feature-ic" aria-hidden="true">💰</div>
                <h3>Operador / Facturación</h3>
                <p className="muted">Tarifas por empresa, honorarios médicos, facturas y márgenes en un panel.</p>
              </article>
            </div>
          </div>
        </section>

        {/* Diferenciadores */}
        <section className="lp-seccion lp-seccion-alt" id="diferenciadores" aria-labelledby="lp-dif-title">
          <div className="lp-seccion-inner">
            <h2 id="lp-dif-title">Por qué Ausentia</h2>
            <div className="lp-grid-auto">
              <article className="tarjeta lp-feature">
                <h3>Doble firma</h3>
                <p className="muted">Firma electrónica del médico por código de un solo uso (OTP) y firma grafométrica del trabajador, cada una con su naturaleza jurídica.</p>
              </article>
              <article className="tarjeta lp-feature">
                <h3>Privacidad por diseño</h3>
                <p className="muted">Doble PDF con control de acceso: RRHH no ve el diagnóstico; el servicio médico sí.</p>
              </article>
              <article className="tarjeta lp-feature">
                <h3>Habilitación auditada del médico</h3>
                <p className="muted">DDJJ firmada + documento de identidad verificados por el operador antes de habilitar la firma.</p>
              </article>
              <article className="tarjeta lp-feature">
                <h3>Offline-first</h3>
                <p className="muted">Funciona sin señal; modo contingencia con acta firmada. El acta se sube manualmente al recuperar la señal.</p>
              </article>
              <article className="tarjeta lp-feature">
                <h3>Facturación multi-empresa</h3>
                <p className="muted">Tarifas, honorarios y márgenes por empresa, con bitácora encadenada por hash.</p>
              </article>
            </div>
          </div>
        </section>

        {/* Oferta */}
        <section className="lp-seccion" id="oferta" aria-labelledby="lp-oferta-title">
          <div className="lp-seccion-inner">
            <h2 id="lp-oferta-title">Tres formas de ofrecer Ausentia</h2>
            <div className="lp-grid-auto">
              <article className="tarjeta lp-feature">
                <span className="insignia verde">SaaS Empresa</span>
                <h3>Para equipos con prestador propio</h3>
                <p className="muted">
                  Portal para RRHH y servicio médico, app de médicos auditores y trazabilidad por control.
                </p>
              </article>
              <article className="tarjeta lp-feature">
                <span className="insignia azul">Operado</span>
                <h3>Para delegar el circuito completo</h3>
                <p className="muted">
                  Coordinación de visitas, asignación de profesionales, seguimiento, facturación y honorarios en una
                  sola plataforma.
                </p>
              </article>
              <article className="tarjeta lp-feature">
                <span className="insignia ambar">Prestadores</span>
                <h3>Para medicina laboral multiempresa</h3>
                <p className="muted">
                  Gestión de clientes, tarifas, médicos, comprobantes y márgenes, con marca Ausentia o convivencia con
                  la marca del prestador.
                </p>
              </article>
            </div>
          </div>
        </section>

        {/* Cumplimiento */}
        <section className="lp-seccion lp-seccion-alt" id="cumplimiento" aria-labelledby="lp-leg-title">
          <div className="lp-seccion-inner lp-legal">
            <h2 id="lp-leg-title">Pensado para el marco legal argentino</h2>
            <div className="lp-legal-strip">
              <span className="insignia azul">Art. 210 LCT</span>
              <span className="insignia azul">Decreto 407/2026</span>
              <span className="insignia ambar">Listo para firma digital (PAdES + TSA) vía certificador licenciado</span>
              <span className="insignia gris">Ley 25.326 datos personales</span>
            </div>
            <p className="muted lp-legal-nota">
              Arquitectura preparada para integrarse con un certificador licenciado y una TSA habilitada (Ley 25.506).
              En la versión actual el médico autoriza con firma electrónica (código OTP); la firma digital con
              certificador y sello de tiempo se activa al contratar esos servicios.
            </p>
          </div>
        </section>

        {/* CTA final */}
        <section className="lp-seccion lp-cta-final" aria-labelledby="lp-cta-title">
          <div className="lp-seccion-inner">
            <h2 id="lp-cta-title">Conocé Ausentia en acción</h2>
            <p className="lp-lead">Recorré la demo con datos de ejemplo.</p>
            <div className="lp-cta-row lp-cta-row-centro">
              <CtaDemo className="lp-cta">Entrar a la demo</CtaDemo>
            </div>
          </div>
        </section>
      </main>

      <footer className="lp-footer">
        <div className="lp-seccion-inner">
          <div className="lp-footer-top">
            <div>
              <strong>Ausentia</strong> · by AtuelSoft
            </div>
            <nav className="lp-footer-links" aria-label="Pie">
              <CtaDemo className="lp-footer-demo">Ingresar a la demo</CtaDemo>
            </nav>
          </div>
          <p className="lp-footer-nota">
            Demostración con datos de ejemplo. La firma digital (PAdES), el sello de tiempo (TSA) y las integraciones con
            los servicios oficiales están <strong>simulados</strong>; en producción se integran con un certificador
            licenciado y una TSA habilitada. No constituye asesoramiento legal ni un documento con validez jurídica.
          </p>
        </div>
      </footer>
    </div>
  );
}

/** Patrón decorativo 7×7 para la maqueta de código (no es un QR real). */
const QR_PATRON: boolean[] = [
  true, true, true, false, true, true, true,
  true, false, true, false, false, false, true,
  true, false, true, true, true, false, true,
  false, false, false, true, false, true, false,
  true, true, false, false, true, true, true,
  true, false, true, false, false, false, false,
  true, true, true, false, true, false, true,
];
